import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useSearchParams } from "react-router";

import { getPossibleQueueMembers, getQueueLog, getQueues } from "@/api/stackApi";
import { HELP_LOGS_QUEUES } from "@/constants/HelpUrls";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import useDebounce from "@/utils/hooks/useDebounce";
import useNow from "@/utils/hooks/useNow";
import Valkyrie, {
  viCalendar,
  viCheck,
  viCircleDashed,
  viCircleXmark,
  viList,
  viListBarChart,
  viListClock,
  viLocationPinDot,
  viMagnifyingGlass,
  viPerson,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  InputBase,
  Pagination,
  Paper,
  Stack,
} from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  differenceInDays,
  differenceInSeconds,
  endOfDay,
  format,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import MultiFilterDropdown from "../_Components/MultiFilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import QueueLogItem from "./QueueLogItem";

export default function QueueLog() {
  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_QUEUES));
  }, [dispatch]);

  // Search field
  const [extensionSearch, setExtensionSearch] = useState(searchParams.get("extensions"));
  const debouncedExtensionSearch = useDebounce(extensionSearch, 500);

  // Filter preparations
  const queuesQuery = useQuery({
    queryKey: [pbx.context, "queues"],
    queryFn: getQueues,
    refetchInterval: 10 * 1000 * 60,
  });
  const possibleQueueMembers = useQuery({
    queryKey: [pbx.context, "queues.members"],
    queryFn: getPossibleQueueMembers,
    refetchInterval: 10 * 1000 * 60,
  });

  // Filters
  const now = useNow();
  const { page, order, orderBy, startDate, endDate, queues, agents, events, grouping, extensions } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      order: searchParams.get("order") === "asc" ? "asc" : "desc",
      orderBy: ["date", "callid", "queuename", "agent", "event"].includes(searchParams.get("orderBy"))
        ? searchParams.get("orderBy")
        : "date",
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
      queues: searchParams.get("queues")
        ? searchParams
            .get("queues")
            .split(",")
            .map((queue) => Number(queue))
        : [],
      agents: searchParams.get("agents") ? searchParams.get("agents").split(",") : [],
      events: searchParams.get("events") ? searchParams.get("events").split(",") : [],
      grouping: ["callid", "event"].includes(searchParams.get("grouping")) ? searchParams.get("grouping") : "callid",
      extensions: searchParams.get("extensions") || "",
    }),
    [now, searchParams],
  );

  const isFiltering = useMemo(
    () =>
      !(
        page === 0 &&
        order === "desc" &&
        orderBy === "date" &&
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        queues.length === 0 &&
        agents.length === 0 &&
        events.length === 0 &&
        grouping === "callid" &&
        extensions === ""
      ),
    [now, page, order, orderBy, startDate, endDate, grouping, queues.length, agents.length, events.length, extensions],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Check if the selected date range is invalid (either more than 31 days + 1 hour (because DST) or non-positive duration)
  const invalidDateRange =
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) > 31 * 24 * 60 * 60 + 60 * 60 ||
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) <= 0;

  // Setup default selected queues, select 10 if there are up to 10 queues in the PBX
  useEffect(() => {
    if (queuesQuery.isFetched && queuesQuery.data.length <= 10 && queues.length === 0) {
      setFilter("queues", queuesQuery.data.map((queue) => queue.id).join(","));
    }
  }, [queues.length, queuesQuery.data, queuesQuery.isFetched, setFilter]);

  // Queries
  const pauseRefresh = !isWithinInterval(now, { start: parseISO(startDate), end: parseISO(endDate) });
  const queueLogQuery = useQuery({
    queryKey: [
      pbx.context,
      "log.queue",
      page,
      order,
      orderBy,
      startDate,
      endDate,
      queues,
      agents,
      events,
      debouncedExtensionSearch,
      grouping,
    ],
    queryFn: () =>
      getQueueLog({
        queues,
        agents,
        startDate,
        endDate,
        events,
        extensions: debouncedExtensionSearch,
        select: grouping,
        page,
        order,
        orderBy,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31 &&
      queues.length > 0 &&
      queues.length < 11,
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  // Data
  const eventTypes = [
    { event: "ABANDON", label: "Abandoned" },
    { event: "ATTENDEDTRANSFER", label: "Attended transfer" },
    { event: "BLINDTRANSFER", label: "Blind transfer" },
    { event: "COMPLETEAGENT", label: "Complete agent" },
    { event: "COMPLETECALLER", label: "Complete caller" },
    { event: "CONNECT", label: "Connect" },
    { event: "ENTERQUEUE", label: "Enter queue" },
    { event: "EXITEMPTY", label: "Exit empty" },
    { event: "EXITWITHTIMEOUT", label: "Exit with timeout" },
    { event: "RINGCANCELED", label: "Ring canceled" },
    { event: "RINGNOANSWER", label: "Ring no answer" },
    { event: "TRANSFER", label: "Transfer" },
  ];

  const queueStatsUrl = useMemo(() => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      queues,
    });

    return `/pbx/${pbx.context}/logs/queue-stats?${params.toString()}`;
  }, [endDate, pbx.context, queues, startDate]);

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Queue log \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar>
        <ClearFilterAction disabled={isFiltering} onClick={() => setExtensionSearch("")} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <MultiFilterDropdown
          id="queues"
          label={{ all: "0 queues", singular: "queue", plural: "queues" }}
          icon={viList}
          options={queuesQuery.data || []}
          value={queues}
          setValue={(value) => setFilter("queues", value.length ? value.join(",") : "")}
          getOptionLabel={(x) => x.name}
          getOptionLabelSecondary={(x) => x.exten}
          getOptionValue={(x) => x.id}
          getAvatarIcon={() => viList}
          searchKeys={["name", "exten"]}
          limit={10}
        />
        <MultiFilterDropdown
          id="agents"
          label={{ all: "All members", singular: "member", plural: "members" }}
          icon={viPerson}
          options={possibleQueueMembers.data || []}
          value={agents}
          setValue={(value) => setFilter("agents", value.length ? value.join(",") : "")}
          getOptionLabel={(x) => x.attributes?.name ?? x.name}
          getOptionLabelSecondary={(x) => x.attributes?.exten}
          getOptionValue={(x) => x.attributes?.exten ?? x.value}
          getAvatarIcon={(x) => (x.type === "customDestination" ? viLocationPinDot : undefined)}
          searchKeys={["attributes.name", "attributes.exten"]}
        />
        <MultiFilterDropdown
          id="events"
          label={{ all: "All events", singular: "event", plural: "events" }}
          icon={viCircleDashed}
          options={eventTypes}
          value={events}
          setValue={(value) => setFilter("events", value.length ? value.join(",") : "")}
          getOptionLabel={(x) => x.label}
          getOptionValue={(x) => x.event}
          noAvatar
          searchKeys={["label", "event"]}
        />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <Button
          startIcon={grouping === "callid" && <Valkyrie icon={viCheck} />}
          color="inherit"
          onClick={() => setFilter("grouping", grouping === "callid" ? "event" : "callid")}
        >
          Group calls
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <InputBase
          type="search"
          placeholder="Extension"
          onChange={(e) => {
            setExtensionSearch(e.target.value);
            searchParams.set("extensions", e.target.value);
            searchParams.set("page", 0);
            setSearchParams(searchParams);
          }}
          value={extensionSearch}
          sx={(theme) => ({
            width: 180,
            height: 36,
            flexShrink: 0,
            backgroundColor: theme.vars.palette.action.hover,
            color: "#000",
            borderRadius: 1,
            ...theme.applyStyles("dark", {
              color: "#fff",
            }),
            ".MuiInputAdornment-root": {
              pl: 1.5,
            },
            ".MuiInputBase-input": {
              py: 0,
              pl: 0,
              pr: 1.5,
              border: "none",
            },
          })}
          startAdornment={
            <InputAdornment position="start">
              <Valkyrie icon={viMagnifyingGlass} />
            </InputAdornment>
          }
        />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <Button startIcon={<Valkyrie icon={viListBarChart} />} component={NavLink} to={queueStatsUrl} color="inherit">
          Statistics
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <RefreshActions
          onClick={() => queueLogQuery.refetch()}
          isRefreshing={queueLogQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "min-content repeat(2, auto) min-content repeat(3, auto)",
            gap: 1,
          }}
        >
          <TableHead>
            <TableHeading id="date" setFilter={setFilter} order={order} orderBy={orderBy}>
              Time
            </TableHeading>
            <TableHeading align="right">Duration</TableHeading>
            <TableHeading id="callerid">Caller</TableHeading>
            <TableHeading id="positionWhenLeft">Position</TableHeading>
            <TableHeading id="queuename" setFilter={setFilter} order={order} orderBy={orderBy}>
              Queue
            </TableHeading>
            <TableHeading id="agent" setFilter={setFilter} order={order} orderBy={orderBy}>
              Member
            </TableHeading>
            <TableHeading id="event" setFilter={setFilter} order={order} orderBy={orderBy}>
              Event
            </TableHeading>
          </TableHead>
          {queues.length === 0 ? (
            <MessageStack icon={<Valkyrie icon={viList} />} message="Select at least one queue to get started." />
          ) : invalidDateRange ? (
            <MessageStack icon={<Valkyrie icon={viCalendar} />} message="The current date range is not valid." />
          ) : queues.length >= 11 ? (
            <MessageStack icon={<Valkyrie icon={viList} />} message="You have to many queues selected." color="error" />
          ) : queueLogQuery.isFetched ? (
            <>
              {queueLogQuery.data?.qdr.length >= 1 ? (
                <>
                  {grouping === "callid" ? (
                    <Stack
                      sx={{ mb: 6.5, gap: 1, display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1" }}
                    >
                      {queueLogQuery.data?.qdr.map((queueCall, callKey) => (
                        <Card
                          key={callKey}
                          sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1" }}
                        >
                          {queueCall.map((queueEvent, eventKey) => (
                            <QueueLogItem key={eventKey} queueEvent={queueEvent} />
                          ))}
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Card sx={{ mb: 6.5, display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1" }}>
                      {queueLogQuery.data?.qdr.map((queueCall) => (
                        <>
                          {queueCall.map((queueEvent, eventKey) => (
                            <QueueLogItem key={eventKey} queueEvent={queueEvent} />
                          ))}
                        </>
                      ))}
                    </Card>
                  )}
                </>
              ) : (
                <MessageStack icon={<Valkyrie icon={viListClock} />} message="No data for your current filters..." />
              )}
            </>
          ) : queueLogQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={queueLogQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>

        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Paper
            variant="acrylic"
            sx={{
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box>
              {page * 50 + 1}-
              {(page + 1) * 50 > queueLogQuery.data?.count ? queueLogQuery.data?.count : (page + 1) * 50} of{" "}
              {queueLogQuery.data?.count}
            </Box>
          </Paper>
          {Math.ceil(queueLogQuery.data?.count / 50) > 1 && (
            <Pagination count={Math.ceil(queueLogQuery.data?.count / 50)} page={page + 1} onChange={handlePage} />
          )}
        </FabBar>
      </Container>
    </Page>
  );
}
